/**
 * Custom error classes for calendar API operations
 * These provide consistent error messages for Sentry grouping
 * while preserving context data for debugging
 */

import { BaseApiFetchError } from '../../utility/error/BaseApiFetchError'
import { BaseApiValidationError } from '../../utility/error/BaseApiValidationError'

export class CalendarFetchError extends BaseApiFetchError {
  public override readonly endpoint: 'availability' | 'pricing'

  constructor(
    endpoint: 'availability' | 'pricing',
    url: string,
    params: Record<string, any>,
    originalError: any,
  ) {
    // Use calendar-specific endpoint name for Sentry grouping
    const endpointName = `calendar-${endpoint}`
    super(endpointName, url, params, originalError)

    // Override name for backwards compatibility using Object.defineProperty like CustomError
    Object.defineProperty(this, 'name', {
      value: 'CalendarFetchError',
      enumerable: true,
    })
    this.endpoint = endpoint
  }

  /**
   * Get calendar-specific context data for Sentry or logging
   * Network diagnostics are now provided by BaseApiFetchError
   */
  override getContext() {
    const baseContext = super.getContext()

    return {
      ...baseContext,
      // Calendar-specific context
      endpoint: this.endpoint,

      // Legacy fields for backward compatibility with existing dashboards
      // TODO: Remove requestUrl/requestParams once dashboards are updated to use url/params
      requestUrl: this.url,
      requestParams: (() => {
        try {
          return JSON.stringify(this.params)
        } catch {
          return '[unserializable]'
        }
      })(),
    }
  }
}

export class CalendarValidationError extends BaseApiValidationError {
  public override readonly endpoint: 'availability' | 'pricing'

  constructor(
    endpoint: 'availability' | 'pricing',
    url: string,
    params: Record<string, any>,
    rawResponse: any,
    originalError: any,
  ) {
    // Use calendar-specific endpoint name for Sentry grouping
    const endpointName = `calendar-${endpoint}`
    super(endpointName, url, params, rawResponse, originalError)

    // Override name for backwards compatibility using Object.defineProperty like CustomError
    Object.defineProperty(this, 'name', {
      value: 'CalendarValidationError',
      enumerable: true,
    })
    this.endpoint = endpoint
  }
}
